import { useRememberedTab } from '../modules/core/index.js'
import { useWorkerRoster } from '../modules/roster/index.js'
import { WorkerSearch } from '../modules/roster/index.js'
import { RecalculateScoresCard } from '../modules/scoring/index.js'
import { ClosePeriodCard } from '../modules/scoring/index.js'
import { Tabs } from '../modules/scoring/index.js'
import { MessageWorkersCard } from '../modules/messages/index.js'

interface ToolsProps {
  facilityId: string
  /** How many facilities this person can see, for the search's wording. */
  facilityCount: number
  /** The worker search across facilities: an operator's; a facility administrator's Tools tab goes without it (the user's call, 17 October 2026). */
  showWorkerSearch?: boolean
}

type ToolsTab = 'search' | 'message' | 'recalculate' | 'close'

const TAB_LABELS: Record<ToolsTab, string> = {
  search: 'Worker search',
  message: 'Message workers',
  recalculate: 'Recalculate scores',
  close: 'Close period',
}

/** The tab this browser last chose, kept as a convenience; a blocked or empty store opens on the first tab. */
const STORAGE_KEY = 'autonome.tools.tab'

/**
 * The facility's Tools tab, one section per sub-tab: the operator's worker search across the facilities they can see,
 * messaging workers (the facility's own words to one worker or the roster, SMS plan 3.7) and the two scoring actions -
 * recalculate every running score, and close a period, both disabled while the roster is empty. The month's build, the
 * board after the release, the census and publishing shifts moved to the Dashboard's Today and Month tabs on
 * 25 October 2026 (facility operations dashboard plan §3). Every section stays mounted, so a half-filled form survives
 * a switch.
 */
export function Tools({ facilityId, facilityCount, showWorkerSearch = true }: ToolsProps) {
  const roster = useWorkerRoster(facilityId)
  const hasWorkers = (roster.data?.length ?? 0) > 0

  const available: ToolsTab[] = [...(showWorkerSearch ? (['search'] as const) : []), 'message', 'recalculate', 'close']
  // A link into Tools while it is already open (the Month card on a facility whose Tools tab was shown) still lands on its tab.
  const { current, choose } = useRememberedTab(STORAGE_KEY, available)

  return (
    <div className="flex flex-col gap-6 pt-6 pb-8">
      <div className="max-w-5xl mx-auto w-full px-6">
        <Tabs className="" tabs={available.map((id) => ({ id, label: TAB_LABELS[id] }))} active={current} onChange={choose} />
      </div>
      {showWorkerSearch && (
        <div hidden={current !== 'search'}>
          <WorkerSearch key={facilityId} facilityCount={facilityCount} />
        </div>
      )}
      <div className="max-w-5xl mx-auto w-full px-6">
        <div hidden={current !== 'message'}>
          <MessageWorkersCard key={facilityId} facilityId={facilityId} />
        </div>
        <div hidden={current !== 'recalculate'}>
          <RecalculateScoresCard facilityId={facilityId} hasWorkers={hasWorkers} />
        </div>
        <div hidden={current !== 'close'}>
          <ClosePeriodCard facilityId={facilityId} hasWorkers={hasWorkers} />
        </div>
      </div>
    </div>
  )
}
